import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';

import { ActivityLogging } from './ActivityLogging.js';
import {
  createSupabaseOneTimeActivityRepository,
  createSupabaseRecurringActivityRepository,
} from './activityRepository.js';
import { createBrowserCompletionLog } from './completions/index.js';
import { OneTimeActivities } from './OneTimeActivities.js';
import { createBrowserPushNotifications } from './pushNotifications/index.js';
import { PushNotificationSettings } from './PushNotificationSettings.js';
import { RecurringActivities } from './RecurringActivities.js';
import { SignIn } from './SignIn.js';
import { supabase } from './supabase.js';

type SessionState = Session | null | undefined;

const recurringActivityRepository = createSupabaseRecurringActivityRepository(supabase);
const oneTimeActivityRepository = createSupabaseOneTimeActivityRepository(supabase);
const completionLog = createBrowserCompletionLog(supabase);
const pushNotifications = createBrowserPushNotifications(supabase);

export const App = (): React.JSX.Element => {
  const [session, setSession] = useState<SessionState>(undefined);
  const [signOutProblem, setSignOutProblem] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession);
    });

    void supabase.auth.getSession().then(({ data: current, error }) => {
      if (!active) return;
      setSession(error ? null : current.session);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signOut = async (): Promise<void> => {
    setSignOutProblem(null);
    const { error } = await supabase.auth.signOut();
    if (error) {
      setSignOutProblem(error.message);
      return;
    }
  };

  if (session === undefined) return <main />;

  if (session === null) {
    return (
      <main>
        <h1>life-rpg</h1>
        <SignIn />
      </main>
    );
  }

  return (
    <main>
      <h1>life-rpg</h1>
      <p>Signed in as {session.user.email ?? 'the configured user'}.</p>
      <button type="button" onClick={() => void signOut()}>
        Sign out
      </button>
      {signOutProblem !== null && <p role="alert">{signOutProblem}</p>}
      <ActivityLogging
        activityRepository={recurringActivityRepository}
        completionLog={completionLog}
      />
      <OneTimeActivities repository={oneTimeActivityRepository} />
      <RecurringActivities repository={recurringActivityRepository} />
      <PushNotificationSettings notifications={pushNotifications} />
    </main>
  );
};
